/**
 * MoonGlobe — MapLibre GL JS globe rendering the lunar surface.
 *
 * Uses OpenPlanetary Moon basemap tiles via CARTO CDN (CORS-enabled, free).
 * Hillshaded albedo imagery with nomenclature labels and landing sites.
 * Lazily initialized on first Moon button click to avoid wasting memory/bandwidth.
 * Auto-rotates at 40% of Earth's speed (educational detail: the Moon rotates slower).
 */
import maplibregl from 'maplibre-gl'

const MOON_TILE_URL =
  'https://cartocdn-gusc.global.ssl.fastly.net/opmbuilder/api/v1/map/named/opm-moon-basemap-v0-1/all/{z}/{x}/{y}.png'

const MOON_INITIAL_VIEW = {
  center: [0, 0],
  zoom: 1.5,
  pitch: 0,
  bearing: 0,
}

// 40% of Earth's AUTO_ROTATE_SPEED (3 deg/s)
const MOON_ROTATE_SPEED = 1.2

let moonMap = null
let autoRotate = false
let autoRotateRAF = null
let lastTimestamp = null
const isFlying = false

/**
 * Create and return the Moon MapLibre instance.
 * Returns a Promise that resolves once the map fires its 'load' event.
 */
function initMoonGlobe() {
  if (moonMap) return Promise.resolve(moonMap)

  const container = document.getElementById('moon-globe-container')
  if (!container) throw new Error('Missing #moon-globe-container')

  const style = {
    version: 8,
    sources: {
      'moon-tiles': {
        type: 'raster',
        tiles: [MOON_TILE_URL],
        tileSize: 256,
        maxzoom: 6,
        attribution: '&copy; <a href="https://www.openplanetary.org">OpenPlanetary</a>',
      },
    },
    layers: [
      {
        id: 'background',
        type: 'background',
        paint: { 'background-color': '#000000' },
      },
      {
        id: 'moon-raster',
        type: 'raster',
        source: 'moon-tiles',
        paint: { 'raster-opacity': 1.0 },
      },
    ],
  }

  moonMap = new maplibregl.Map({
    container,
    style,
    center: MOON_INITIAL_VIEW.center,
    zoom: MOON_INITIAL_VIEW.zoom,
    pitch: MOON_INITIAL_VIEW.pitch,
    bearing: MOON_INITIAL_VIEW.bearing,
    antialias: true,
    maxPitch: 85,
  })

  // Enable globe projection
  moonMap.on('style.load', () => {
    moonMap.setProjection({ type: 'globe' })
  })

  // Stop auto-rotation on user interaction
  moonMap.on('mousedown', () => stopAutoRotate())
  moonMap.on('touchstart', () => stopAutoRotate())
  moonMap.on('wheel', () => stopAutoRotate())

  return new Promise((resolve) => {
    moonMap.on('load', () => {
      console.log('[MoonGlobe] Loaded — OpenPlanetary basemap tiles')
      resolve(moonMap)
    })
  })
}

/**
 * Return the existing Moon MapLibre instance, or null if not yet initialized.
 */
function getMoonMap() {
  return moonMap
}

/**
 * Check whether the Moon globe has been initialized.
 */
function isInitialized() {
  return moonMap !== null
}

/**
 * Destroy the Moon map instance and free GPU/memory resources.
 */
function destroyMoonGlobe() {
  stopAutoRotate()
  if (moonMap) {
    moonMap.remove()
    moonMap = null
  }
}

// ── Auto-rotation ──────────────────────────────────────────

function startAutoRotate() {
  if (autoRotateRAF || !moonMap) return
  autoRotate = true
  lastTimestamp = performance.now()

  const animate = (timestamp) => {
    if (!autoRotate) return
    if (isFlying) {
      autoRotateRAF = requestAnimationFrame(animate)
      return
    }

    const delta = (timestamp - lastTimestamp) / 1000
    lastTimestamp = timestamp

    const center = moonMap.getCenter()
    moonMap.setCenter([center.lng + MOON_ROTATE_SPEED * delta, center.lat])

    autoRotateRAF = requestAnimationFrame(animate)
  }

  autoRotateRAF = requestAnimationFrame(animate)
}

function stopAutoRotate() {
  autoRotate = false
  if (autoRotateRAF) {
    cancelAnimationFrame(autoRotateRAF)
    autoRotateRAF = null
  }
}

// ── Markers & Popups ─────────────────────────────────────────

const activeMarkers = []
let activePopup = null

// Color scheme for lunar marker categories
const MARKER_COLORS = {
  // Landing site categories
  crewed: '#FFD700', // gold
  robotic: '#00BFFF', // deep sky blue
  rover: '#7CFC00', // lawn green
  sample_return: '#FF69B4', // hot pink
  impact: '#FF4500', // orange red
  // Feature types
  mare: '#6495ED', // cornflower blue
  crater: '#DDA0DD', // plum
  mountain: '#98FB98', // pale green
  basin: '#F0E68C', // khaki
  valley: '#87CEEB', // sky blue
  escarpment: '#D2B48C', // tan
  rille: '#B0C4DE', // light steel blue
  default: '#FFFFFF',
}

/**
 * Add markers to the Moon globe for the given GeoJSON features.
 * @param {Feature[]} features - GeoJSON features with Point geometry
 * @param {string} markerType - 'landing' | 'feature' for color selection
 */
function addMarkers(features, markerType = 'landing') {
  if (!moonMap) return
  clearMarkers()

  for (const f of features) {
    const p = f.properties
    const [lng, lat] = f.geometry.coordinates

    // Determine color
    const colorKey = markerType === 'landing' ? p.category || 'default' : p.type || 'default'
    const color = MARKER_COLORS[colorKey] || MARKER_COLORS.default

    // Create marker element
    const el = document.createElement('div')
    el.className = 'moon-marker'
    el.style.cssText = `
      width: 14px; height: 14px;
      background: ${color};
      border: 2px solid rgba(255,255,255,0.9);
      border-radius: 50%;
      cursor: pointer;
      box-shadow: 0 0 8px ${color}80;
      transition: transform 0.15s ease;
    `
    el.addEventListener('mouseenter', () => {
      el.style.transform = 'scale(1.5)'
    })
    el.addEventListener('mouseleave', () => {
      el.style.transform = 'scale(1)'
    })

    // Build popup HTML
    const popupHTML = buildPopupHTML(f)
    const popup = new maplibregl.Popup({
      closeButton: true,
      closeOnClick: true,
      maxWidth: '280px',
      className: 'moon-popup-container',
      offset: 12,
    }).setHTML(popupHTML)

    const marker = new maplibregl.Marker({ element: el })
      .setLngLat([lng, lat])
      .setPopup(popup)
      .addTo(moonMap)

    activeMarkers.push(marker)
  }
}

/**
 * Build popup HTML for a lunar feature.
 */
function buildPopupHTML(feature) {
  const p = feature.properties
  let html = `<div class="moon-popup">`
  html += `<strong>${p.name}</strong>`

  if (p.englishName) {
    html += `<br><em>${p.englishName}</em>`
  }

  if (p.mission && p.date) {
    html += `<br><span class="moon-popup-meta">${p.country} · ${p.date}</span>`
  }

  if (p.crew && p.crew.length > 0) {
    html += `<br><span class="moon-popup-meta">Crew: ${p.crew.join(', ')}</span>`
  }

  if (p.type && !p.mission) {
    const typeLabel = p.type.charAt(0).toUpperCase() + p.type.slice(1)
    let sizeInfo = ''
    if (p.diameter_km) sizeInfo = ` · ${p.diameter_km} km`
    if (p.height_m) sizeInfo = ` · ${(p.height_m / 1000).toFixed(1)} km high`
    if (p.length_km) sizeInfo = ` · ${p.length_km} km long`
    html += `<br><span class="moon-popup-meta">${typeLabel}${sizeInfo}</span>`
  }

  if (p.description) {
    html += `<br><span class="moon-popup-desc">${p.description}</span>`
  }

  html += `</div>`
  return html
}

/**
 * Show a popup for a lunar feature (programmatic, e.g. from router).
 */
function showPopup(feature, lngLat) {
  if (!moonMap) return
  if (activePopup) activePopup.remove()

  activePopup = new maplibregl.Popup({
    closeButton: true,
    closeOnClick: true,
    maxWidth: '280px',
    className: 'moon-popup-container',
    offset: 12,
  })
    .setLngLat(lngLat)
    .setHTML(buildPopupHTML(feature))
    .addTo(moonMap)
}

/**
 * Remove all markers from the Moon globe.
 */
function clearMarkers() {
  for (const m of activeMarkers) m.remove()
  activeMarkers.length = 0
  if (activePopup) {
    activePopup.remove()
    activePopup = null
  }
}

export {
  initMoonGlobe,
  getMoonMap,
  isInitialized,
  destroyMoonGlobe,
  startAutoRotate,
  stopAutoRotate,
  addMarkers,
  clearMarkers,
  showPopup,
  MARKER_COLORS,
}
